import type { MockedResponse } from "@apollo/client/testing";
import { CREATE_COMMISSION_PROGRAM_CONSENT_MUTATION } from "../consents/mutations";
import {
  catalogApolloMocks,
  catalogMockResults,
  catalogVariableMockResolvers,
  buildCampaignsMockData,
  commissionCampaignsApolloMock,
  commissionCampaignsMockData,
  commissionEnrollmentsApolloMock,
  commissionEnrollmentsMockData,
  commissionPartnerApolloMock,
  commissionPartnersApolloMock,
  commissionPartnersMockData,
  commissionProgramsApolloMock,
  commissionProgramsMockData,
  createCommissionCampaignApolloMock,
  createCommissionPartnerApolloMock
} from "./catalogMocks";
import {
  commissionAllocationAgreementLiveApolloMock,
  commissionAllocationAgreementsApolloMock,
  commissionProgramConsentsApolloMock,
  consentsApolloMocks,
  consentsVariableMockResolvers
} from "./consentsMocks";
import {
  commissionIngestBatchesApolloMock,
  ingestApolloMocks,
  ingestMockResults,
  ingestVariableMockResolvers
} from "./ingestMocks";
import {
  commissionLinesApolloMock,
  linesApolloMocks,
  linesMockResults,
  linesVariableMockResolvers
} from "./linesMocks";
import {
  buildFundingBatchesMockData,
  commissionFundingBatchesApolloMock,
  fundingApolloMocks,
  fundingMockResults,
  fundingVariableMockResolvers
} from "./fundingMocks";
import {
  commissionReleaseBatchesApolloMock,
  releaseApolloMocks,
  releaseMockResults,
  releaseVariableMockResolvers
} from "./releaseMocks";
import {
  commissionActivityEventsApolloMock,
  historyApolloMocks,
  historyMockResults,
  historyVariableMockResolvers
} from "./historyMocks";
import {
  buildPersonGrpBenefitsMockData,
  personGrpBenefitsApolloMock,
  personGrpBenefitsApolloMocks,
  personGrpBenefitsMockData,
  personGrpBenefitsMockResults,
  personGrpBenefitsVariableMockResolvers
} from "./personGrpBenefitsMocks";
import type { MockResolver } from "./mockUtils";
import { withTypenames } from "./mockUtils";

export {
  buildCampaignsMockData,
  buildFundingBatchesMockData,
  buildPersonGrpBenefitsMockData,
  commissionActivityEventsApolloMock,
  commissionAllocationAgreementLiveApolloMock,
  commissionAllocationAgreementsApolloMock,
  commissionCampaignsApolloMock,
  commissionCampaignsMockData,
  commissionEnrollmentsApolloMock,
  commissionEnrollmentsMockData,
  commissionFundingBatchesApolloMock,
  commissionIngestBatchesApolloMock,
  commissionLinesApolloMock,
  commissionPartnerApolloMock,
  commissionPartnersApolloMock,
  commissionPartnersMockData,
  commissionProgramConsentsApolloMock,
  commissionProgramsApolloMock,
  commissionProgramsMockData,
  commissionReleaseBatchesApolloMock,
  createCommissionCampaignApolloMock,
  createCommissionPartnerApolloMock,
  personGrpBenefitsApolloMock,
  personGrpBenefitsMockData
};

export const createCommissionProgramConsentApolloMock: MockedResponse = {
  request: { query: CREATE_COMMISSION_PROGRAM_CONSENT_MUTATION },
  maxUsageCount: Infinity,
  variableMatcher: () => true,
  result: {
    data: {
      commissionCreateProgramConsent: {
        consent: withTypenames(
          {
            id: "commission-consent-created",
            commissionCampaignId: "commission-campaign-2026-fairway",
            crmPersonId: "person-ada",
            attributionModel: "FACILITY",
            ownership: "OWNER",
            startsAt: "2026-01-01T00:00:00Z",
            endsAt: null,
            status: "ACTIVE"
          },
          "BackofficeCommissionProgramConsent"
        )
      }
    }
  }
};

/** All commission catalog query mocks — use in Vitest, Storybook, or dev mock link. */
export const commissionCatalogApolloMocks: MockedResponse[] = [
  ...catalogApolloMocks,
  ...ingestApolloMocks,
  ...linesApolloMocks,
  ...fundingApolloMocks,
  ...releaseApolloMocks,
  ...historyApolloMocks,
  ...consentsApolloMocks,
  ...personGrpBenefitsApolloMocks
];

export const commissionCatalogMockResults: Record<string, Record<string, unknown>> = {
  ...catalogMockResults,
  ...ingestMockResults,
  ...linesMockResults,
  ...fundingMockResults,
  ...releaseMockResults,
  ...historyMockResults,
  ...personGrpBenefitsMockResults
};

export const commissionVariableMockResolvers: Record<string, MockResolver> = {
  ...catalogVariableMockResolvers,
  ...consentsVariableMockResolvers,
  ...ingestVariableMockResolvers,
  ...linesVariableMockResolvers,
  ...fundingVariableMockResolvers,
  ...releaseVariableMockResolvers,
  ...historyVariableMockResolvers,
  ...personGrpBenefitsVariableMockResolvers
};
